import { Route, Routes } from 'react-router-dom';

import { AcceptInvitationPage } from '@/features/auth/AcceptInvitationPage';
import { ForgotPasswordPage } from '@/features/auth/ForgotPasswordPage';
import { LoginPage } from '@/features/auth/LoginPage';
import { GuestRoute, ProtectedRoute } from '@/features/auth/ProtectedRoute';
import { RegisterPage } from '@/features/auth/RegisterPage';
import { ResetPasswordPage } from '@/features/auth/ResetPasswordPage';
import { InvitationsPage } from '@/features/members/InvitationsPage';
import { MembersPage } from '@/features/members/MembersPage';
import { CatalogPage } from '@/features/catalog/CatalogPage';
import { CustomerDetailPage } from '@/features/customers/CustomerDetailPage';
import { CustomersPage } from '@/features/customers/CustomersPage';
import { ApprovalsPage } from '@/features/approvals/ApprovalsPage';
import { NotificationSettingsPage } from '@/features/notifications/NotificationSettingsPage';
import { QuotationPage } from '@/features/quotations/QuotationPage';
import { QuotationsPage } from '@/features/quotations/QuotationsPage';
import { RfqDetailPage } from '@/features/rfqs/RfqDetailPage';
import { RfqWizardPage } from '@/features/rfqs/RfqWizardPage';
import { RfqsPage } from '@/features/rfqs/RfqsPage';
import { RolesPage } from '@/features/rbac/RolesPage';
import { AppShell } from '@/layouts/AppShell';
import { DashboardPage } from '@/features/reporting/DashboardPage';
import { NotFoundPage } from '@/pages/NotFoundPage';
import { SystemStatusPage } from '@/pages/SystemStatusPage';

/**
 * Route table.
 *
 * Three tiers mirroring the API:
 *   public     reachable signed out
 *   guest      redirects away if already signed in
 *   protected  requires a session; the API re-checks on every request
 *
 * Route guards are a UX convenience, never the security boundary.
 */
export function App() {
  return (
    <Routes>
      {/* Public */}
      <Route path="/status" element={<SystemStatusPage />} />
      <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
      {/* Deliberately NOT guest-only: an already-signed-in user following an
          invitation link should join, not be bounced to the dashboard. */}
      <Route path="/accept-invitation/:token" element={<AcceptInvitationPage />} />

      {/* Guest-only */}
      <Route element={<GuestRoute />}>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      </Route>

      {/* Authenticated */}
      <Route element={<ProtectedRoute />}>
        <Route element={<AppShell />}>
          <Route path="/" element={<DashboardPage />} />
          <Route path="/members" element={<MembersPage />} />
          <Route path="/invitations" element={<InvitationsPage />} />
          <Route path="/roles" element={<RolesPage />} />
          <Route path="/customers" element={<CustomersPage />} />
          <Route path="/customers/:id" element={<CustomerDetailPage />} />
          <Route path="/catalogue" element={<CatalogPage />} />
          <Route path="/rfqs" element={<RfqsPage />} />
          <Route path="/rfqs/new" element={<RfqWizardPage />} />
          {/* AFTER /rfqs/new, so the literal segment is not captured as an id. */}
          <Route path="/rfqs/:id" element={<RfqDetailPage />} />
          <Route path="/quotations" element={<QuotationsPage />} />
          <Route path="/quotations/:id" element={<QuotationPage />} />
          <Route path="/approvals" element={<ApprovalsPage />} />
          {/* No permission gate: notification settings are about you, not about the tenant. */}
          <Route path="/settings/notifications" element={<NotificationSettingsPage />} />
        </Route>
      </Route>

      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}
